import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * @typedef {Object} NojuPdpStickyAtcRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} stickyAtcButton
 * @property {HTMLElement} sizeSheet
 * @property {HTMLElement} sizeSheetBody
 */

/**
 * Sticky NOJU add-to-cart bar. Uses Horizon's intersection-observer pattern but
 * watches the in-page ATC block and proxies clicks to the main product form button.
 * When size is still needed, opens a size sheet above the sticky button.
 *
 * @extends {Component<NojuPdpStickyAtcRefs>}
 */
class NojuPdpStickyAtc extends Component {
  requiredRefs = ['stickyBar', 'stickyAtcButton', 'sizeSheet', 'sizeSheetBody'];

  /** @type {IntersectionObserver | null} */
  #atcIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {boolean} */
  #hiddenByFooter = false;

  /** @type {boolean} */
  #sizeSheetOpen = false;

  /** @type {HTMLButtonElement | null} */
  #mainAtcButton = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  
  #ensureStickySizeStyles() {
    if (document.getElementById('noju-sticky-size-styles')) return;
    const style = document.createElement('style');
    style.id = 'noju-sticky-size-styles';
    style.textContent = `
.noju-pdp-sticky-atc__sheet{display:none;width:100%;margin:0 0 12px;padding:0}
.noju-pdp-sticky-atc__bar[data-size-sheet="true"] .noju-pdp-sticky-atc__sheet,
.noju-pdp-sticky-atc__sheet:not([hidden]){display:block!important}
.noju-pdp-sticky-atc__sheet-title{margin:0 0 10px!important;padding:0!important;font-size:12px!important;font-weight:600!important;letter-spacing:.08em!important;text-transform:uppercase!important;color:#666!important;line-height:1.3!important}
.noju-pdp-sticky-atc__size-group + .noju-pdp-sticky-atc__size-group{margin-top:14px}
.noju-pdp-sticky-atc__size-group-label{margin:0 0 8px!important;font-size:13px!important;font-weight:500!important;color:#111!important}
.noju-pdp-sticky-atc__size-options{display:grid!important;grid-template-columns:repeat(var(--noju-sticky-size-columns,4),minmax(0,1fr))!important;gap:8px!important;width:100%!important;margin:0!important;padding:0!important;border:none!important}
button.noju-pdp-sticky-atc__size-button,.noju-pdp-sticky-atc__size-button{
appearance:none!important;-webkit-appearance:none!important;box-sizing:border-box!important;display:flex!important;align-items:center!important;justify-content:center!important;width:100%!important;min-width:0!important;min-height:48px!important;height:auto!important;margin:0!important;padding:12px 4px!important;border:1px solid #d9d9d9!important;border-radius:4px!important;background:#fff!important;color:#000!important;font:inherit!important;font-size:14px!important;font-weight:400!important;line-height:1!important;letter-spacing:normal!important;text-align:center!important;text-transform:none!important;text-decoration:none!important;box-shadow:none!important;outline:none!important;cursor:pointer!important}
button.noju-pdp-sticky-atc__size-button[data-selected="true"],button.noju-pdp-sticky-atc__size-button[aria-pressed="true"],
.noju-pdp-sticky-atc__size-button[data-selected="true"],.noju-pdp-sticky-atc__size-button[aria-pressed="true"]{border-color:#111!important;background:#111!important;color:#fff!important}
button.noju-pdp-sticky-atc__size-button[data-unavailable="true"],.noju-pdp-sticky-atc__size-button[data-unavailable="true"]{opacity:.45!important;color:#666!important;text-decoration:line-through!important;background:#fff!important}
`;
    document.head.appendChild(style);
  }

  connectedCallback() {
    super.connectedCallback();
    this.#ensureStickySizeStyles();

    this.#setupIntersectionObservers();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#onCartComplete, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });

    this.refs.stickyAtcButton.addEventListener('click', this.#onStickyClick, { signal });
    window.addEventListener('resize', this.#onResize, { signal });
  }

  #onResize = () => {
    if (this.#isStuck) {
      this.#updateScrollPadding();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#atcIntersectionObserver?.disconnect();
    this.#footerObserver?.disconnect();
    this.#abortController.abort();
  }

  #setupIntersectionObservers() {
    const section = this.closest('.shopify-section');
    const atcBlock = section?.querySelector('.noju-pdp__block--atc');
    const productForm = this.#getProductForm();
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    if (!atcBlock || !productForm || !footer) return;

    this.#mainAtcButton = productForm.querySelector('[data-noju-atc-button]');

    this.#atcIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (entry.isIntersecting) {
        this.#hide();
        return;
      }

      if (this.#hiddenByFooter) return;

      const rect = entry.target.getBoundingClientRect();
      if (rect.bottom < 0) {
        this.#show();
      } else {
        this.#hide();
      }
    });

    const footerSentinel =
      document.querySelector('.footer-localization-section') ??
      document.querySelector('.noju-footer-links-section') ??
      footer;

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting) {
          this.#hiddenByFooter = true;
          this.#hide();
        } else if (this.#hiddenByFooter) {
          this.#hiddenByFooter = false;
          this.#syncVisibilityFromAtcBlock(atcBlock);
        }
      },
      { rootMargin: '0px 0px 80px 0px', threshold: 0 }
    );

    this.#atcIntersectionObserver.observe(atcBlock);
    this.#footerObserver.observe(footerSentinel);
    this.#syncVisibilityFromAtcBlock(atcBlock);
  }

  /**
   * Show sticky only after main ATC has scrolled above the viewport.
   * @param {Element} atcBlock
   */
  #syncVisibilityFromAtcBlock(atcBlock) {
    if (this.#hiddenByFooter) return;

    const rect = atcBlock.getBoundingClientRect();
    const isInView = rect.top < window.innerHeight && rect.bottom > 0;

    if (isInView || rect.bottom >= 0) {
      this.#hide();
    } else {
      this.#show();
    }
  }

  /** @param {MouseEvent} event */
  #onStickyClick = (event) => {
    event.preventDefault();

    const mainButton = this.#mainAtcButton ?? this.#getProductForm()?.querySelector('[data-noju-atc-button]');
    if (!(mainButton instanceof HTMLButtonElement)) return;

    const mode = mainButton.dataset.nojuAtcMode || 'add';

    if (mode === 'blocked') return;

    // Always open the size sheet first so sticky ATC is the size-choice path.
    if (!this.#sizeSheetOpen) {
      this.#toggleSizeSheet(true);
      return;
    }

    if (mode === 'select' || !this.#hasCompleteSizeSelection()) {
      return;
    }

    if (mainButton.disabled && mode !== 'notify') return;

    this.#toggleSizeSheet(false);
    mainButton.dataset.puppet = 'true';
    mainButton.click();
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.#sizeSheetOpen) return;
    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.refs.stickyBar.contains(target)) return;
    this.#toggleSizeSheet(false);
  };

  /** @param {CustomEvent} _event */
  #onCartComplete = (_event) => {
    const mainButton = this.#mainAtcButton ?? this.#getProductForm()?.querySelector('[data-noju-atc-button]');
    if (mainButton instanceof HTMLButtonElement) {
      mainButton.dataset.puppet = 'false';
    }
    this.#toggleSizeSheet(false);
  };

  /** @param {import('./events').VariantUpdateEvent} event */
  #onVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    if (variant?.id) {
      this.dataset.currentVariantId = String(variant.id);
    }

    const productForm = this.#getProductForm();
    if (productForm) {
      this.#mainAtcButton = productForm.querySelector('[data-noju-atc-button]');
    }

    this.#syncFromMainAtc();
    if (this.#sizeSheetOpen) {
      this.#renderSizeSheet();
    }
  };

  /**
   * Mirror label, price, mode and disabled state from the main ATC button.
   * Called from noju-pdp.js after buybox sync as well.
   */
  syncFromMainAtc() {
    this.#syncFromMainAtc();
  }

  #syncFromMainAtc() {
    const mainButton = this.#getProductForm()?.querySelector('[data-noju-atc-button]');
    const stickyButton = this.refs.stickyAtcButton;
    if (!(mainButton instanceof HTMLButtonElement) || !(stickyButton instanceof HTMLButtonElement)) return;

    const mainLabel = mainButton.closest('add-to-cart-component')?.querySelector('[data-noju-atc-label]');
    const mainPrice = mainButton.closest('add-to-cart-component')?.querySelector('[data-noju-atc-price]');
    const stickyLabel = this.querySelector('[data-noju-sticky-atc-label]');
    const stickyPrice = this.querySelector('[data-noju-sticky-atc-price]');

    if (mainLabel instanceof HTMLElement && stickyLabel instanceof HTMLElement) {
      stickyLabel.textContent = mainLabel.textContent;
    }

    if (mainPrice instanceof HTMLElement && stickyPrice instanceof HTMLElement) {
      stickyPrice.textContent = mainPrice.textContent;
      stickyPrice.classList.toggle('hidden', mainPrice.classList.contains('hidden'));
    }

    const mode = mainButton.dataset.nojuAtcMode || 'add';
    stickyButton.dataset.nojuAtcMode = mode;
    // Keep sticky clickable in select mode so the size sheet can open.
    stickyButton.disabled = mode === 'blocked';
  }

  /** @param {boolean} [forceOpen] */
  #toggleSizeSheet(forceOpen) {
    const open = typeof forceOpen === 'boolean' ? forceOpen : !this.#sizeSheetOpen;
    this.#sizeSheetOpen = open;
    this.refs.stickyBar.dataset.sizeSheet = open ? 'true' : 'false';
    this.refs.sizeSheet.hidden = !open;
    this.refs.stickyAtcButton.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (open) {
      this.#renderSizeSheet();
    }

    if (this.#isStuck) {
      this.#updateScrollPadding();
      requestAnimationFrame(() => this.#updateScrollPadding());
    }
  }

  #renderSizeSheet() {
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector('variant-picker.noju-pdp__variant-picker');
    const body = this.refs.sizeSheetBody;
    if (!(body instanceof HTMLElement)) return;

    body.replaceChildren();

    if (!(picker instanceof HTMLElement)) {
      body.textContent = this.dataset.selectSizeLabel || 'Select size';
      return;
    }

    const fieldsets = picker.querySelectorAll('fieldset.noju-pdp__size-options');
    fieldsets.forEach((fieldset, index) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return;

      const group = document.createElement('div');
      group.className = 'noju-pdp-sticky-atc__size-group';

      const header = fieldset.closest('.noju-pdp__size-group')?.querySelector('.noju-pdp__size-label');
      const legend = fieldset.querySelector('legend');
      const labelText =
        header?.textContent?.trim() ||
        legend?.textContent?.trim() ||
        (fieldsets.length > 1 ? `Size ${index + 1}` : '');

      if (labelText && fieldsets.length > 1) {
        const title = document.createElement('p');
        title.className = 'noju-pdp-sticky-atc__size-group-label';
        title.textContent = labelText;
        group.appendChild(title);
      }

      const row = document.createElement('div');
      row.className = 'noju-pdp-sticky-atc__size-options';
      const colCount = String(fieldset.querySelectorAll('input[type="radio"]').length || 4);
      row.style.setProperty('--noju-sticky-size-columns', colCount);
      row.style.cssText +=
        `display:grid;grid-template-columns:repeat(${colCount},minmax(0,1fr));gap:8px;width:100%;margin:0;padding:0;border:none;`;

      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;

        const text =
          input.closest('label')?.querySelector('[data-key="variant-option-text"]')?.textContent?.trim() ||
          input.value;
        const available = input.dataset.optionAvailable !== 'false';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'noju-pdp-sticky-atc__size-button';
        btn.textContent = text;
        btn.style.cssText =
          'appearance:none;-webkit-appearance:none;box-sizing:border-box;display:flex;align-items:center;justify-content:center;width:100%;min-width:0;min-height:48px;margin:0;padding:12px 4px;border:1px solid #d9d9d9;border-radius:4px;background:#fff;color:#000;font:inherit;font-size:14px;font-weight:400;line-height:1;letter-spacing:normal;text-align:center;text-transform:none;text-decoration:none;box-shadow:none;cursor:pointer;';
        btn.dataset.fieldsetIndex = input.dataset.fieldsetIndex || String(index);
        if (input.dataset.optionValueId) btn.dataset.optionValueId = input.dataset.optionValueId;
        if (input.dataset.variantId) btn.dataset.variantId = input.dataset.variantId;
        btn.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
        if (input.checked) btn.dataset.selected = 'true';
        if (input.checked) {
          btn.style.background = '#111';
          btn.style.borderColor = '#111';
          btn.style.color = '#fff';
        }
        if (!available) {
          btn.dataset.unavailable = 'true';
          btn.setAttribute('aria-disabled', 'true');
        }

        btn.addEventListener('click', () => this.#onSheetSizeClick(input, available));
        row.appendChild(btn);
      });

      group.appendChild(row);
      body.appendChild(group);
    });
  }

  /**
   * @param {HTMLInputElement} input
   * @param {boolean} available
   */
  #onSheetSizeClick(input, available) {
    if (!available) {
      // Mirror OOS: select then let main ATC go to notify if that is the flow.
      input.click();
      this.#syncFromMainAtc();
      this.#renderSizeSheet();
      return;
    }

    if (!input.checked) {
      input.click();
    }

    // Wait a tick for variant picker + buybox sync, then add if ready.
    requestAnimationFrame(() => {
      this.#syncFromMainAtc();
      this.#renderSizeSheet();

      const mainButton = this.#mainAtcButton ?? this.#getProductForm()?.querySelector('[data-noju-atc-button]');
      if (!(mainButton instanceof HTMLButtonElement)) return;

      const mode = mainButton.dataset.nojuAtcMode || 'add';
      if (mode === 'add' && !mainButton.disabled) {
        this.#toggleSizeSheet(false);
        mainButton.dataset.puppet = 'true';
        mainButton.click();
        return;
      }

      if (mode === 'select') {
        // Core set may need a second dimension — keep sheet open.
        return;
      }

      if (mode === 'notify') {
        this.#toggleSizeSheet(false);
        mainButton.dataset.puppet = 'true';
        mainButton.click();
      }
    });
  }

  #hasCompleteSizeSelection() {
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector('variant-picker.noju-pdp__variant-picker');
    if (!(picker instanceof HTMLElement)) return true;

    const fieldsets = picker.querySelectorAll('fieldset.noju-pdp__size-options');
    if (fieldsets.length === 0) return true;

    return Array.from(fieldsets).every((fieldset) => {
      if (!(fieldset instanceof HTMLFieldSetElement)) return false;
      return Boolean(fieldset.querySelector('input[type="radio"]:checked'));
    });
  }

  #show() {
    this.#isStuck = true;
    this.refs.stickyBar.dataset.stuck = 'true';
    this.#updateScrollPadding();
  }

  #hide() {
    this.#isStuck = false;
    this.refs.stickyBar.dataset.stuck = 'false';
    this.#toggleSizeSheet(false);
    this.#clearScrollPadding();
  }

  #updateScrollPadding() {
    const bar = this.refs.stickyBar;
    if (!(bar instanceof HTMLElement)) return;

    const height = Math.ceil(bar.getBoundingClientRect().height);
    // Keep Shopify Inbox chat activator above sticky ATC (+ size sheet).
    const chatGap = 12;
    const chatOffset = height + chatGap;
    document.documentElement.style.setProperty('--noju-sticky-atc-height', `${height}px`);
    document.documentElement.style.setProperty('--shopify-chat-activator-offset', `${chatOffset}px`);
    document.body.style.paddingBottom = `${height}px`;
  }

  #clearScrollPadding() {
    document.documentElement.style.removeProperty('--noju-sticky-atc-height');
    document.documentElement.style.removeProperty('--shopify-chat-activator-offset');
    document.body.style.paddingBottom = '';
  }

  /** @returns {HTMLElement | null} */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const section = this.closest('.shopify-section');
    if (!section) return null;

    const sectionId = section.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }
}

if (!customElements.get('noju-pdp-sticky-atc')) {
  customElements.define('noju-pdp-sticky-atc', NojuPdpStickyAtc);
}

/**
 * @param {HTMLElement} section
 */
export function syncNojuPdpStickyAtc(section) {
  const sticky = section.querySelector('noju-pdp-sticky-atc');
  if (sticky && typeof sticky.syncFromMainAtc === 'function') {
    sticky.syncFromMainAtc();
  }
}
